<script setup lang="ts">
import { computed, getCurrentInstance, ref, unref, watch } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';
import { updateSingleScanShared } from '@/_core/entities/report/api/report.endpoints';
import { useDownloadPdfReport } from '@/_core/entities/report/api/useDownloadPdfReport';
import { useLlmFeedbackFlow } from '@/_core/entities/llm-feedback/api/useLlmFeedbackFlow';
import {
  useActiveFolderName,
  useAppVersionPagePayload,
  useReportPayload,
  useSingleScanPagePayload,
} from '@/_core/entities/report/utils/report.utils';
import { useFetchAppVersionReport } from '@/_core/entities/report/api/useFetchAppVersionReport';
import { useFetchSingleScanReport } from '@/_core/entities/report/api/useFetchSingleScanReport';
import { useFetchSingleScanFolders } from '@/_core/entities/report/api/useFetchSingleScanFolders';
import { useFetchAppVersionFolders } from '@/_core/entities/report/api/useFetchAppVersionFolders';
import { useFetchSingleScanPage } from '@/_core/entities/report/api/useFetchSingleScanPage';
import { useFetchAppVersionPage } from '@/_core/entities/report/api/useFetchAppVersionPage';
import { useFetchComplianceSummary } from '@/_core/entities/report/api/useFetchComplianceSummary';
import type { TComplianceSummaryPayload } from '@/_core/entities/report/models/types';
import ReportComplianceRequirements from '@/_core/widgets/ReportComplianceRequirements/ReportComplianceRequirements.vue';
import type {
  TSingleScanFoldersPayload,
  TAppVersionFoldersPayload,
} from '@/_core/entities/report/models/types';
import { FEEDBACK_TYPE } from '@/_core/entities/llm-feedback/models/types';
import ReportBreadcrumbs from '@/_core/entities/report/ui/ReportBreadcrumbs/ReportBreadcrumbs.vue';
import ReportGeneralDetails from '@/_core/entities/report/ui/ReportGeneralDetails/ReportGeneralDetails.vue';
import ReportVulnerabilityStats from '@/_core/entities/report/ui/ReportVulnerabilityStats/ReportVulnerabilityStats.vue';
import ReportSidebar from '@/_core/entities/report/ui/ReportSidebar/ReportSidebar.vue';
import ReportSummary from '@/_core/entities/report/ui/ReportSummary/ReportSummary.vue';
import LlmFeedbackRating from '@/_core/shared/ui/LlmFeedbackRating/LlmFeedbackRating.vue';
import LlmFeedbackCommentModal from '@/_core/shared/ui/LlmFeedbackCommentModal/LlmFeedbackCommentModal.vue';
import Accordion from '@/_core/shared/ui/Accordion/Accordion.vue';
import ReportSection from '@/_core/entities/report/ui/ReportSection/ReportSection.vue';
import ReportVulnerabilitiesList from '@/_core/widgets/ReportVulnerabilitiesList/ReportVulnerabilitiesList.vue';
import ReportPageContentSkeleton from '@/_core/entities/report/ui/ReportPageContentSkeleton/ReportPageContentSkeleton.vue';
import TrialOverlay from '@/_core/entities/vulnerability/ui/TrialOverlay/TrialOverlay.vue';
import { technicalSummaryContent as mockTechnicalSummary } from '@/_core/entities/report/ui/ReportSummary/technicalSummaryContent';
import { nonTechnicalSummaryContent as mockNonTechnicalSummary } from '@/_core/entities/report/ui/ReportSummary/nonTechnicalSummaryContent';
import { REPORT_PAGE_LIMIT } from '@/_core/entities/report/models/consts';
import { downloadPdfReport } from '@/services/pdfReportDownloader';

const instance = getCurrentInstance();
const routeRef = computed(() => instance?.proxy.$route);

const reportType = computed(() => (unref(routeRef)?.params?.jobId ? 'single-scan' : 'app-version'));

const route = computed(() => instance?.proxy.$route);

const currentPage = computed(() => Number(route.value?.query?.page) || 1);
const totalPages = computed(() => Math.ceil((pageData.value?.count ?? 0) / REPORT_PAGE_LIMIT));

const activeFolderName = useActiveFolderName(routeRef);

const versionsReportPayloadRef = useReportPayload('app-version', routeRef);
const singleScanPayloadRef = useReportPayload('single-scan', routeRef);

function getComplianceQuery() {
  const query = unref(routeRef)?.query;
  const requirementId = Array.isArray(query?.complianceRequirementId)
    ? query?.complianceRequirementId[0]
    : query?.complianceRequirementId;
  const itemId = Array.isArray(query?.complianceRequirementItemId)
    ? query?.complianceRequirementItemId[0]
    : query?.complianceRequirementItemId;
  return {
    complianceRequirementId: requirementId || undefined,
    complianceRequirementItemId: itemId || undefined,
  };
}

const singleScanFoldersPayloadRef = computed<TSingleScanFoldersPayload | null>(() => {
  const params = unref(routeRef)?.params;
  if (!params?.jobId) return null;
  return { jobId: params.jobId, ...getComplianceQuery() };
});

const appVersionFoldersPayloadRef = computed<TAppVersionFoldersPayload | null>(() => {
  const params = unref(routeRef)?.params;
  if (!params?.integrationId || !params?.versionId) return null;
  return {
    integrationId: params.integrationId,
    versionId: params.versionId,
    ...getComplianceQuery(),
  };
});

const singleScanQuery = useFetchSingleScanReport(singleScanPayloadRef);
const appVersionQuery = useFetchAppVersionReport(versionsReportPayloadRef);

const singleScanFoldersQuery = useFetchSingleScanFolders(singleScanFoldersPayloadRef);
const appVersionFoldersQuery = useFetchAppVersionFolders(appVersionFoldersPayloadRef);

const reportDetailsQuery = reportType.value === 'single-scan' ? singleScanQuery : appVersionQuery;
const foldersQuery =
  reportType.value === 'single-scan' ? singleScanFoldersQuery : appVersionFoldersQuery;

const {
  data: reportData,
  isLoading: isReportLoading,
  isFetching: isReportFetching,
} = reportDetailsQuery;
const {
  data: foldersData,
  isLoading: isFolderDataLoading,
  isFetching: isFolderDataFetching,
} = foldersQuery;

const activeFolder = computed(() =>
  foldersData.value?.find((f) => f.name === activeFolderName.value)
);
const isFolderEmpty = computed(() => activeFolder.value?.isEmptyFolder ?? false);

watch(activeFolderName, () => {
  foldersQuery.refetch();

  if (currentPage.value === 1) return;

  setPage(1);
});

const rawSingleScanPagePayloadRef = useSingleScanPagePayload(routeRef, activeFolderName);
const rawAppVersionPagePayloadRef = useAppVersionPagePayload(routeRef, activeFolderName);

const singleScanPagePayloadRef = computed(() =>
  isFolderDataFetching.value || isFolderEmpty.value ? null : rawSingleScanPagePayloadRef.value
);
const appVersionPagePayloadRef = computed(() =>
  isFolderDataFetching.value || isFolderEmpty.value ? null : rawAppVersionPagePayloadRef.value
);

const complianceSummaryPayloadRef = computed<TComplianceSummaryPayload | null>(() => {
  const params = unref(routeRef)?.params;
  const jobId = params?.jobId ?? reportData.value?.id;
  const integrationId = params?.integrationId ?? reportData.value?.integrationId ?? undefined;
  if (!jobId || !integrationId) return null;
  return { jobId, integrationId, folderName: activeFolderName.value };
});

const { data: complianceSummaryData, isLoading: isComplianceSummaryLoading } =
  useFetchComplianceSummary(complianceSummaryPayloadRef);

function handleClearComplianceFilter() {
  const router = instance?.proxy.$router;
  const currentQuery = instance?.proxy.$route.query ?? {};
  router
    ?.push({
      query: {
        ...currentQuery,
        page: '1',
        complianceRequirementId: undefined,
        complianceRequirementItemId: undefined,
      },
    })
    .catch((err) => {
      if (err?.name !== 'NavigationDuplicated') throw err;
    });
}

function handleViewComplianceFindings(payload: { requirementId?: string; itemId?: string }) {
  const router = instance?.proxy.$router;
  const currentQuery = instance?.proxy.$route.query ?? {};
  router
    ?.push({
      query: {
        ...currentQuery,
        page: '1',
        complianceRequirementId: payload.itemId ? undefined : payload.requirementId,
        complianceRequirementItemId: payload.itemId,
      },
    })
    .catch((err) => {
      if (err?.name !== 'NavigationDuplicated') throw err;
    });
  const el = document.getElementById('report-vulnerabilities-list');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const singleScanPageQuery = useFetchSingleScanPage(singleScanPagePayloadRef);
const appVersionPageQuery = useFetchAppVersionPage(appVersionPagePayloadRef);

const pageQuery = reportType.value === 'single-scan' ? singleScanPageQuery : appVersionPageQuery;
const { data: pageData, isLoading: isPageLoading, isFetching: isPageFetching } = pageQuery;
const isDownloadingReport = ref(false);

const isSampleReport = computed(
  () => reportData.value?.type === 'SAMPLE' || reportData.value?.type === 'VERSION_SAMPLE'
);

const isTrialWithTechnicalSummary = computed(
  () =>
    reportType.value === 'app-version' &&
    !!reportData.value?.isTrial &&
    !!reportData.value?.hasTechnicalSummary
);

const technicalSummary = computed(() => {
  if (!reportData.value) return null;
  if (isSampleReport.value || isTrialWithTechnicalSummary.value) return mockTechnicalSummary;
  return reportData.value.technicalSummary;
});
const nonTechnicalSummary = computed(() => {
  if (!reportData.value) return null;

  if (isSampleReport.value) {
    return mockNonTechnicalSummary;
  }

  return reportData.value.nonTechnicalSummary;
});

watch(reportData, (newReportData) => {
  if (!newReportData) return;

  document.title = `${newReportData.fileName} — Report | Oversecured`;
});

function setPage(page: number) {
  instance?.proxy.$router.push({
    query: {
      ...instance?.proxy.$route.query,
      page: String(page),
    },
  });
}

const queryClient = useQueryClient();
const toast = useToast();

const { mutate: downloadPdf, isPending: isDownloading } = useDownloadPdfReport();

function handleDownload() {
  if (!reportData.value) return;

  const filename = reportData.value.fileName + '.pdf';
  const folderName = activeFolderName.value;
  const vulnerabilityCategoriesIds = reportData.value.statistics.percentageByType.map((v) => v.id);

  if (reportType.value === 'single-scan') {
    downloadPdf({
      isSingleScan: true,
      jobId: reportData.value.id,
      filename,
      folderName,
      vulnerabilityCategoriesIds,
    });
    return;
  }

  const params = unref(routeRef)?.params;

  if (!params?.integrationId || !params?.branchName || !params?.versionId) return;

  downloadPdf({
    isSingleScan: false,
    integrationId: params.integrationId,
    branchName: params.branchName,
    versionId: params.versionId,
    filename,
    folderName,
    vulnerabilityCategoriesIds,
  });
}

const { mutate: toggleShared, isPending: isTogglingShared } = useMutation({
  mutationFn: (shared: boolean) => updateSingleScanShared(reportData.value!.id, shared),
  onSuccess: (_, shared) => {
    queryClient.invalidateQueries({ queryKey: ['singleScanReport'] });
    toast.success({
      message: shared
        ? 'Report is now shared. Anyone with the link can view it.'
        : 'Report is now private',
    });
  },
  onError: () => {
    toast.error({ message: 'Failed to update report sharing' });
  },
});

const hasTechnicalSummary = computed(() => !!reportData.value?.technicalSummary);
const hasNonTechnicalSummary = computed(() => !!reportData.value?.nonTechnicalSummary);

const serverTechnicalRating = computed(() => reportData.value?.technicalFeedback ?? null);
const serverNonTechnicalRating = computed(() => reportData.value?.nonTechnicalFeedback ?? null);

const {
  isCommentModalOpen,
  closeCommentModal,
  handleRate,
  handleCommentSubmit,
  getEffectiveRating: getEffectiveJobRating,
  isPending: isJobFeedbackPending,
} = useLlmFeedbackFlow({
  getContentId: () => reportData.value?.id,
  getQueryKey: () =>
    reportType.value === 'single-scan' ? ['singleScanReport'] : ['versionReport'],
  getServerRating: (contentType) =>
    contentType === FEEDBACK_TYPE.JOB_TECHNICAL
      ? serverTechnicalRating.value
      : serverNonTechnicalRating.value,
  isContentAvailable: (contentType) =>
    (contentType === FEEDBACK_TYPE.JOB_TECHNICAL
      ? hasTechnicalSummary.value
      : hasNonTechnicalSummary.value) || isSampleReport.value,
  skipSubmit: () => isSampleReport.value,
});
</script>

<template>
  <div>
    <div class="tw:flex tw:gap-4 tw:py-4 tw:md:pl-5 tw:md:pr-4">
      <div class="tw:max-md:hidden">
        <ReportSidebar :foldersData="foldersData" :isFetchingFolders="isFolderDataLoading" />
      </div>

      <ReportPageContentSkeleton v-if="isReportLoading || isReportFetching" />

      <div v-else-if="reportData" class="tw:grow tw:min-w-0">
        <ReportBreadcrumbs
          :reportData="reportData"
          :integrationId="routeRef?.params?.integrationId"
          :branchName="routeRef?.params?.branchName"
        />

        <ReportSection classNames="tw:mt-4">
          <ReportGeneralDetails
            :reportData="reportData"
            :isSingleScan="reportType === 'single-scan'"
            :isDownloading="isDownloading"
            :isTogglingShared="isTogglingShared"
            @download="handleDownload"
            @toggleShared="toggleShared"
          />
        </ReportSection>

        <div class="tw:mt-2 tw:lg:mt-4">
          <ReportVulnerabilityStats :reportData="reportData" />
        </div>

        <div
          class="tw:mt-2 tw:lg:mt-4 tw:flex tw:flex-col tw:gap-2 tw:xl:flex-row tw:xl:gap-4 tw:xl:items-start"
        >
          <TrialOverlay title="Technical Summary" :isContentUnlocked="!isTrialWithTechnicalSummary">
            <ReportSection v-if="technicalSummary || nonTechnicalSummary" classNames="tw:flex-1">
              <Accordion>
                <div class="tw:flex tw:gap-4 tw:lg:gap-12">
                  <div v-if="technicalSummary" class="tw:flex-1">
                    <ReportSummary
                      title="Technical summary"
                      :tags="['Developers', 'Security engineers']"
                      :content="technicalSummary"
                    />
                    <div class="tw:mt-4 tw:flex tw:justify-end">
                      <LlmFeedbackRating
                        :rating="getEffectiveJobRating(FEEDBACK_TYPE.JOB_TECHNICAL)"
                        :isPending="isJobFeedbackPending"
                        @rate="handleRate(FEEDBACK_TYPE.JOB_TECHNICAL, $event)"
                      />
                    </div>
                  </div>
                  <!-- Keep in mind there's a duplicate below for mobile screens -->
                  <div v-if="nonTechnicalSummary" class="tw:flex-1 tw:hidden tw:md:block">
                    <ReportSummary
                      title="Non-technical summary"
                      :tags="['Managers']"
                      :content="nonTechnicalSummary"
                    />
                    <div class="tw:mt-4 tw:flex tw:justify-end">
                      <LlmFeedbackRating
                        :rating="getEffectiveJobRating(FEEDBACK_TYPE.JOB_NON_TECHNICAL)"
                        :isPending="isJobFeedbackPending"
                        @rate="handleRate(FEEDBACK_TYPE.JOB_NON_TECHNICAL, $event)"
                      />
                    </div>
                  </div>
                </div>
              </Accordion>
            </ReportSection>
          </TrialOverlay>
        </div>

        <div class="tw:mt-2 tw:lg:mt-4 tw:block tw:md:hidden" v-if="nonTechnicalSummary">
          <ReportSection>
            <Accordion>
              <!-- Keep in mind there's a duplicate above for desktop screens -->
              <ReportSummary
                title="Non-technical summary"
                :tags="['Managers']"
                :content="nonTechnicalSummary"
              />
              <div class="tw:mt-4 tw:flex tw:justify-end">
                <LlmFeedbackRating
                  :rating="getEffectiveJobRating(FEEDBACK_TYPE.JOB_NON_TECHNICAL)"
                  :isPending="isJobFeedbackPending"
                  @rate="handleRate(FEEDBACK_TYPE.JOB_NON_TECHNICAL, $event)"
                />
              </div>
            </Accordion>
          </ReportSection>
        </div>

        <LlmFeedbackCommentModal
          :isOpen="isCommentModalOpen"
          :isPending="isJobFeedbackPending"
          @submit="handleCommentSubmit"
          @close="closeCommentModal"
        />

        <ReportSection classNames="tw:mt-2 tw:lg:mt-4">
          <ReportComplianceRequirements
            :summary="complianceSummaryData"
            :isLoading="isComplianceSummaryLoading"
            :integrationId="
              routeRef?.params?.integrationId ?? reportData?.integrationId ?? undefined
            "
            :selectedRequirementId="
              typeof routeRef?.query?.complianceRequirementId === 'string'
                ? routeRef.query.complianceRequirementId
                : undefined
            "
            :selectedItemId="
              typeof routeRef?.query?.complianceRequirementItemId === 'string'
                ? routeRef.query.complianceRequirementItemId
                : undefined
            "
            @viewFindings="handleViewComplianceFindings"
          />
        </ReportSection>

        <div id="report-vulnerabilities-list">
        <ReportSection classNames="tw:mt-2 tw:lg:mt-4">
          <ReportVulnerabilitiesList
            :foldersData="foldersData"
            :pageData="pageData"
            :isDataLoading="
              isPageLoading || isPageFetching || isFolderDataLoading || isFolderDataFetching
            "
            :isFolderEmpty="isFolderEmpty"
            :setPage="setPage"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :complianceSummary="complianceSummaryData"
            :selectedRequirementId="
              typeof routeRef?.query?.complianceRequirementId === 'string'
                ? routeRef.query.complianceRequirementId
                : undefined
            "
            :selectedItemId="
              typeof routeRef?.query?.complianceRequirementItemId === 'string'
                ? routeRef.query.complianceRequirementItemId
                : undefined
            "
            @complianceSelect="handleViewComplianceFindings"
            @complianceClear="handleClearComplianceFilter"
          />
        </ReportSection>
        </div>
      </div>
    </div>
  </div>
</template>
